import React from "react";
import { Field } from "react-final-form";
import PropTypes from "prop-types";

/**
 * Toggle switch — drop-in replacement for boolean/integer ANO|NE SelectFields.
 *
 * trueValue  — value stored when ON  (default: true)
 * falseValue — value stored when OFF (default: false)
 *
 * Examples:
 *   <SwitchField name="active"   trueValue={1}    falseValue={-1} .../>
 *   <SwitchField name="daily"    trueValue={1}    falseValue={0}  .../>
 *   <SwitchField name="reverse"  trueValue={0}    falseValue={1}  .../>  // reversed mapping
 *   <SwitchField name="enabled"  trueValue={true} falseValue={false} .../>
 */
const SwitchField = (props) => {
    const { name, label, isMandatory, trueValue, falseValue, disabled, note } = props;

    return (
        <Field
            name={name}
            validate={(value) => {
                if (isMandatory && value === undefined) return true;
            }}
            render={({ meta, input }) => {
                // treat null/undefined as OFF
                const isOn = input.value !== undefined && input.value !== null && input.value !== ""
                    ? input.value === trueValue
                    : false;

                const toggle = () => {
                    if (disabled) return;
                    input.onChange(isOn ? falseValue : trueValue);
                    input.onBlur(); // mark touched so validation fires
                };

                const hasError = meta.touched && meta.error;

                return (
                    <div className="w-full flex flex-col text-sm p-1">
                        {/* label row */}
                        <div className="form-label font-bold" style={{paddingTop: "4px", paddingBottom: "4px"}}>
                            {label}
                            {isMandatory && <sup className="text-legendyColor" style={{marginLeft: "2px"}}>*</sup>}
                            {note && <div className="text-sm font-normal text-gray-400">{note}</div>}
                        </div>

                        {/* toggle row */}
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: disabled ? "default" : "pointer",
                                userSelect: "none",
                            }}
                            onClick={!disabled ? toggle : undefined}
                        >
                            {/* toggle */}
                            <div
                                style={{
                                    width: "42px",
                                    height: "24px",
                                    borderRadius: "999px",
                                    background: disabled
                                        ? "#e2e8f0"
                                        : isOn ? "#2563eb" : "#cbd5e1",
                                    position: "relative",
                                    transition: "background 0.18s",
                                    flexShrink: 0,
                                    border: hasError ? "2px solid #ef4444" : "2px solid transparent",
                                    boxSizing: "border-box",
                                }}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "16px",
                                        height: "16px",
                                        borderRadius: "50%",
                                        background: disabled ? "#94a3b8" : "#fff",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                        top: "2px",
                                        left: isOn ? "20px" : "2px",
                                        transition: "left 0.18s",
                                    }}
                                />
                            </div>

                            {/* inline state label */}
                            <div style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: isOn ? "#2563eb" : "#94a3b8",
                            }}>
                                {isOn ? "ANO" : "NE"}
                            </div>
                        </div>

                        {hasError && (
                            <div className="text-red-500 text-xs mt-1">Povinné pole</div>
                        )}
                    </div>
                );
            }}
        />
    );
};

SwitchField.defaultProps = {
    disabled: false,
    isMandatory: false,
    trueValue: true,
    falseValue: false,
};

SwitchField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isMandatory: PropTypes.bool,
    disabled: PropTypes.bool,
    trueValue: PropTypes.any,
    falseValue: PropTypes.any,
    note: PropTypes.string,
};

export default SwitchField;
